import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import ToolTour, { useToolTour, openTour, InfoTip, MarkerKey, ProfitSketch, RecommendedFilters } from '../components/ToolTour'
import EVCard from './EVCard'

const C = {
  border: '#1b2538',
  text: '#f1f5f9',
  muted: '#94a3b8',
  dim: '#64748b',
  green: '#34d399',
}

// Slide 1 visual: a dome of bookies, each with its price for the same
// selection, flowing into one fair price. The one bookie above it drops out
// the bottom as the flagged bet. Plays once each time the slide is shown.
const DOME = [
  // outer row, left to right
  { name: 'Sportsbet', odds: 1.91 }, { name: 'TAB', odds: 1.88 }, { name: 'Ladbrokes', odds: 1.95 },
  { name: 'Unibet', odds: 1.93 }, { name: 'Bet365', odds: 2.2 }, { name: 'Pointsbet', odds: 1.9 },
  { name: 'Betr', odds: 1.87 },
  // inner row, sitting in the gaps of the outer one
  { name: 'Dabble', odds: 1.92 }, { name: 'PalmerBet', odds: 1.94 }, { name: 'BetRight', odds: 1.89 },
  { name: 'NextBet', odds: 1.96 }, { name: 'PickleBet', odds: 1.86 }, { name: 'BetCloud', odds: 1.9 },
]
const FAIR = 2.0
const OUTER = 7

function FairOddsFlow({ isMobile }) {
  const g = isMobile
    ? { W: 300, H: 204, logo: 20, odds: 9.5, curve: 26, inner: 30, hubTop: 102, hubH: 42, cardTop: 156, cardW: 248 }
    : { W: 520, H: 244, logo: 26, odds: 11, curve: 36, inner: 36, hubTop: 130, hubH: 50, cardTop: 194, cardW: 300 }
  const edge = isMobile ? 16 : 30
  const step = (g.W - edge * 2) / (OUTER - 1)
  const domeY = (x) => { const t = ((x - edge) / (g.W - edge * 2)) * 2 - 1; return 2 + t * t * g.curve }
  const nodes = DOME.map((b, i) => {
    const x = i < OUTER ? edge + i * step : edge + (i - OUTER + 0.5) * step
    const y = domeY(x) + (i < OUTER ? 0 : g.inner)
    return { ...b, x, y, value: b.odds > FAIR }
  })
  const hx = g.W / 2
  const lineEnd = g.hubTop + 4
  const path = (n) => {
    const y0 = n.y + g.logo / 2
    const mid = (y0 + lineEnd) / 2 + 10
    return `M ${n.x} ${y0} C ${n.x} ${mid}, ${hx} ${mid - 6}, ${hx} ${lineEnd}`
  }
  // Timeline (s): logos pop, lines draw in, hub lands, then the flagged bet drops out
  const tLines = 0.45, tHub = 1.05, tCard = 1.45

  return (
    <div style={{ position: 'relative', width: g.W, height: g.H, flexShrink: 0 }}>
      <svg width={g.W} height={g.H} viewBox={`0 0 ${g.W} ${g.H}`} style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
        <defs>
          <linearGradient id="evtLine" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#3b82f6" stopOpacity="0.12" />
            <stop offset="1" stopColor="#60a5fa" stopOpacity="0.5" />
          </linearGradient>
          <linearGradient id="evtDrop" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#60a5fa" stopOpacity="0.6" />
            <stop offset="1" stopColor="#34d399" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        {nodes.map((n, i) => (
          <path key={`l${i}`} d={path(n)} pathLength="1" className="tt-draw" fill="none"
            stroke={n.value ? '#34d399' : 'url(#evtLine)'} strokeOpacity={n.value ? 0.55 : 1} strokeWidth={n.value ? 1.6 : 1.2}
            style={{ animationDelay: `${tLines + i * 0.03}s` }} />
        ))}
        {nodes.map((n, i) => (
          <path key={`p${i}`} d={path(n)} pathLength="1" className="tt-flow" fill="none"
            stroke={n.value ? '#34d399' : '#93c5fd'} strokeWidth={n.value ? 2.4 : 1.8} strokeLinecap="round"
            style={{ animationDelay: `${tHub + 0.1}s, ${tHub + 0.1 + ((i * 0.37) % 1.8)}s` }} />
        ))}
        {/* Hub down to the flagged bet */}
        <path d={`M ${hx} ${g.hubTop + g.hubH} L ${hx} ${g.cardTop}`} pathLength="1" className="tt-draw" fill="none"
          stroke="url(#evtDrop)" strokeWidth="2" style={{ animationDelay: `${tCard - 0.15}s`, animationDuration: '0.3s' }} />
      </svg>

      {/* The market */}
      {nodes.map((n, i) => (
        <div key={n.name} className="tt-pop" style={{
          position: 'absolute', left: n.x - 22, top: n.y, width: 44,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? '2px' : '3px',
          animationDelay: `${i * 0.035}s`,
        }}>
          <img src={BOOKMAKER_LOGOS[n.name]} alt="" style={{
            width: g.logo, height: g.logo, borderRadius: '50%', objectFit: 'cover', display: 'block', boxSizing: 'border-box',
            border: `1.5px solid ${n.value ? '#34d399' : 'rgba(96,165,250,0.45)'}`,
            boxShadow: n.value ? '0 0 12px rgba(52,211,153,0.55)' : '0 0 8px rgba(59,130,246,0.25)',
            animation: n.value ? 'ttGreenRing 1.8s ease-out infinite' : undefined, animationDelay: `${tCard}s`,
          }} />
          <span style={{
            fontSize: g.odds, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums',
            color: n.value ? C.green : '#cbd5e1',
            padding: n.value ? '2px 4px' : 0, borderRadius: '4px',
            backgroundColor: n.value ? 'rgba(16,185,129,0.14)' : 'transparent',
          }}>
            {n.odds.toFixed(2)}
          </span>
        </div>
      ))}

      {/* Fair price hub */}
      <div className="tt-rise" style={{
        position: 'absolute', top: g.hubTop, left: '50%', marginLeft: isMobile ? -82 : -100, width: isMobile ? 164 : 200, height: g.hubH, boxSizing: 'border-box',
        display: 'flex', alignItems: 'stretch', borderRadius: '14px', overflow: 'hidden',
        background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: '1px solid rgba(96,165,250,0.45)',
        boxShadow: '0 0 24px rgba(59,130,246,0.22), inset 0 1px 0 rgba(255,255,255,0.05)',
        animationDelay: `${tHub}s`,
      }}>
        {[['Fair odds', FAIR.toFixed(2), C.text], ['Win chance', `${(100 / FAIR).toFixed(0)}%`, '#60a5fa']].map(([label, value, color], i) => (
          <React.Fragment key={label}>
            {i > 0 && <div style={{ width: '1px', backgroundColor: '#1e2a40' }} />}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
              <span style={{ fontSize: isMobile ? '8.5px' : '9.5px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
              <span style={{ fontSize: isMobile ? '16px' : '19px', fontWeight: 800, color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* The bet that beats it */}
      <div className="tt-rise" style={{
        position: 'absolute', top: g.cardTop, left: '50%', marginLeft: -g.cardW / 2, width: g.cardW,
        padding: '1.5px', borderRadius: '14px', boxSizing: 'border-box',
        background: 'linear-gradient(135deg, #34d399 0%, #3b82f6 55%, #34d399 100%)',
        animationDelay: `${tCard}s`,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '10px', padding: isMobile ? '7px 10px' : '8px 12px',
          borderRadius: '12.5px', background: 'linear-gradient(180deg, #0c1a22 0%, #08121c 100%)',
          animation: 'ttGlow 2.2s ease-out infinite', animationDelay: `${tCard + 0.5}s`,
        }}>
          <img src={BOOKMAKER_LOGOS.Bet365} alt="" style={{ width: isMobile ? 24 : 28, height: isMobile ? 24 : 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? '12.5px' : '13.5px', fontWeight: 700, color: C.text, lineHeight: 1.2, whiteSpace: 'nowrap' }}>Melbourne Storm</div>
            <div style={{ fontSize: isMobile ? '10.5px' : '11.5px', fontWeight: 600, color: C.muted, marginTop: '2px', whiteSpace: 'nowrap' }}>To win <span style={{ color: '#475569' }}>·</span> Bet365</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
            <span style={{ fontSize: isMobile ? '17px' : '19px', fontWeight: 800, color: C.green, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>2.20</span>
            <span className="tt-pop" style={{
              fontSize: isMobile ? '9.5px' : '10.5px', fontWeight: 800, color: '#052e1f', backgroundColor: C.green,
              padding: '2px 6px', borderRadius: '999px', animationDelay: `${tCard + 0.35}s`,
            }}>+{((2.2 / FAIR - 1) * 100).toFixed(1)}% EV</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Slide 2 visual: our recommended filter setup. Order keeps Bankroll bottom
// right so its (i) popover opens inside the card.
function BankrollInfo() {
  return (
    <InfoTip label="What bankroll means" title="Your bankroll" align="end">
      The money you've put aside for betting. Every stake on the page is sized from it and your Kelly fraction, so bigger edges get bigger stakes.
      <span style={{ display: 'block', marginTop: '8px' }}>Update it as your bankroll grows or shrinks.</span>
    </InfoTip>
  )
}

const RECOMMENDED = [
  { label: 'Sports', value: 'All' },
  { label: 'Bookmakers', value: 'Your accounts' },
  { label: 'EV %', value: '3% to 20%' },
  { label: 'Odds', value: '1.00 to 4.00' },
  { label: 'Starts in', value: '0 to 24 hrs' },
  { label: 'Kelly fraction', value: '½ Kelly' },
  { label: 'Rounding', value: '$5' },
  { label: 'Bankroll', value: 'Your betting funds', info: <BankrollInfo /> },
]

// Slide 3: the real card, carrying slide 1's bet. $40 is what the page shows
// for it at the default ½ Kelly on a $1,000 bankroll, rounded to $5.
function StakeInfo() {
  return (
    <InfoTip label="How the stake is worked out" title="Your stake">
      Sized from your bankroll and Kelly fraction, so bigger edges get bigger stakes. Change both in Filters.
    </InfoTip>
  )
}

function CardGuide({ isMobile }) {
  // Commence times are naive UTC, the same as the API sends them
  const commence = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 19)
  const opp = {
    sport: 'NRL', match: 'Melbourne Storm vs Penrith Panthers', market_type: 'head_to_head',
    selection: 'Melbourne Storm', team_name: 'Melbourne Storm', commence_time: commence,
    original_odds: 2.2, ev_percentage: 10, kelly_stake: 8.33, bookmakers: [{ name: 'Bet365' }],
  }
  return (
    // zoom (not transform) so the card's layout box shrinks too and the key
    // below stays in the stage
    <div style={{ width: '100%', maxWidth: isMobile ? '256px' : '340px' }}>
      <div style={{ zoom: isMobile ? 0.76 : 0.86 }}>
      <EVCard
        opp={opp}
        className="tt-card"
        evText="+10.00%"
        evClass="bc-ev-badge bc-ev-high"
        oddsText="2.20"
        stakeText="$40"
        markers={{ market: 1, selection: 2, books: 3, odds: 4, ev: 5, stake: 6, track: 7 }}
      />
      </div>
      <MarkerKey isMobile={isMobile} labels={['Market', 'Selection', 'Bookmaker', 'Odds', 'EV', 'Stake', 'Track']} />
    </div>
  )
}

function evTourSlides(isMobile) {
  return [
    {
      title: 'How it works',
      body: 'Every outcome has a true probability. The EV Bot works out the fair odds for thousands of bets. When a bookie offers odds above the fair odds, the bet is flagged and sent straight to you. Take these bets consistently and the profits build over the long run.',
      visual: <FairOddsFlow isMobile={isMobile} />,
    },
    {
      title: 'Set your filters',
      body: 'The default filters show a large number of bets, and every one of them is profitable. Refining your filters helps you get the most out of your time and bankroll. Here\'s our recommended setup. Once it\'s set, tap Save Current and set it as your default so it loads every time.',
      visual: <RecommendedFilters rows={RECOMMENDED} isMobile={isMobile} />,
    },
    {
      title: 'Using the EV page',
      body: (
        <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
          <li>Tap the bookie logo to open the game.</li>
          <li>Check their odds match the card, or better.</li>
          <li>Stake the amount shown <StakeInfo />.</li>
          <li>Tap <strong style={{ color: C.text }}>+</strong> to track it in your Tracker.</li>
        </ol>
      ),
      visual: <CardGuide isMobile={isMobile} />,
    },
    {
      title: 'You\'re ready to go',
      body: 'New bets appear as they\'re found, so check in often. EV betting has big swings in the short term, and losing runs are normal. Stick to the method over hundreds of bets and the edge shows up in your profit. It typically returns 5-15% ROI depending on the bets you take, that\'s $5 to $15 for every $100 staked.',
      visual: <ProfitSketch isMobile={isMobile} headline="5-15% ROI" sub="$5 to $15 for every $100 staked" />,
    },
  ]
}

// Desktop opens it from the page's ?, mobile from PageRouter's header ?; both
// send `bc-open-tour` so neither needs a handle on the hook.
export const openEvTour = () => openTour('ev')

export default function EVTour({ isVisible, isMobile }) {
  const { hasAccess } = useAuth()
  const tour = useToolTour('ev', { isVisible })
  return (
    <ToolTour
      open={tour.open}
      onClose={tour.close}
      slides={evTourSlides(isMobile)}
      eyebrow="Positive EV"
      isMobile={isMobile}
      finalAction={hasAccess('ev') ? { label: 'Get into it' } : { label: 'Unlock with Sports Premium', onClick: () => { window.location.hash = '#/pricing' } }}
      learnMore={{ label: 'Learn more', onClick: () => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'education' })) }}
    />
  )
}
